export {
  blurContentPermissions,
  actionPermissions,
  isValidContentType,
  ACTION_TYPES,
  CONTENT_TYPES,
  LOCKED_LINK_MESSAGE,
  savedContentTypeMapping
}

import { isGoalPremium } from './goal_scores'

const LOCKED_LINK_MESSAGE = 'Resources can be accessed with a district or school membership.'

const ACTION_TYPES = {
  FAVORITE: 'favorite',
  SAVE: 'save',
  DOWNLOAD: 'download',
  COPY: 'copy',
  PRINT: 'print',
  FOLLOW_LINK: 'follow_link',
  SHARE: 'share',
  COPY_STUDENT: 'copy_student',
  DEVELOP_PLW: 'develop_present_levels',
  TRANSLATE: 'translate',
  GENERATE_ASSESSMENT: 'generate_assessment'
}

const CONTENT_TYPES = {
  GOAL: 'goal',
  SAVED_GOAL: 'saved_goal',
  SUPER_PAGE: 'super_page',
  SUPER_PAGE_ASSESSMENT: 'super_page_assessment',
  SAVED_SUPER_PAGE: 'saved_super_page',
  STRATEGY: 'strategy',
  IMPACT_OF_DISABILITY: 'impact_of_disability',
  SAVED_STRATEGY: 'saved_strategy',
  RESOURCE: 'resource',
  ATTACHMENT_LINK: 'attachment_link',
  ATTACHMENT_FILE: 'attachment_file',
  PRESENTLEVEL: 'presentlevel',
  PRESENTLEVEL_STUDENT_SUMMARY: 'pl_student_summary',
  SHARED_STRATEGY: 'shared_strategy',
  STUDENT: 'student',
  SAVED_IMPACT_OF_DISABILITY: 'saved_impact_of_disability'
}

const savedContentTypeMapping = {
  [CONTENT_TYPES.GOAL]: CONTENT_TYPES.SAVED_GOAL,
  [CONTENT_TYPES.SUPER_PAGE]: CONTENT_TYPES.SAVED_SUPER_PAGE,
  [CONTENT_TYPES.STRATEGY]: CONTENT_TYPES.SAVED_STRATEGY,
  [CONTENT_TYPES.IMPACT_OF_DISABILITY]: CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY
}

/*
 * Logic to handle whether content should be blurred or not, and if so for what reason
 * e.g. unsigned in users can't see anything and are prompted to sign up/sign in
 *
 * auth info is an object with the following fields
 * - isAuthenticated
 * - isTrial
 * - isPremium
 * - isSuperAdmin
 * - isLicenseExpired

 * returns an accessInfo object with the following boolean flags:
 * - blur
 * - blurUnauthenticated
 * - blurExpired
 * - blurTrial
 */
function blurContentPermissions(contentType, content, authInfo) {
  var blurUnauthenticated = !authInfo.isAuthenticated
  var blurExpired = authInfo.isLicenseExpired
  var blurTrial = false

  if (
    [
      CONTENT_TYPES.GOAL,
      CONTENT_TYPES.SUPER_PAGE,
      CONTENT_TYPES.SAVED_GOAL,
      CONTENT_TYPES.SAVED_SUPER_PAGE
    ].includes(contentType)
  ) {
    if (authInfo.isPremium || authInfo.isSuperAdmin) {
      blurTrial = false
    } else if (authInfo.isTrial) {
      blurTrial = isGoalPremium(content)
    }
  }

  if (contentType === CONTENT_TYPES.STRATEGY) {
    blurTrial = false
  }

  // demo override flags
  var isDemo = content.demo

  return {
    blur:
      !isDemo &&
      !authInfo.isSuperAdmin &&
      (blurUnauthenticated || blurExpired || blurTrial) &&
      !authInfo.overwriteBlur,
    blurUnauthenticated: !isDemo && blurUnauthenticated,
    blurExpired: !isDemo && !blurUnauthenticated && blurExpired,
    blurTrial: !isDemo && !blurUnauthenticated && !blurExpired && blurTrial,
    demo: isDemo
  }
}

/*
 * Logic to handle whether actions should be restricted or not
 *
 * actionType and contentType - passed in case permissions depend on these types. Returned along with the permissions object so consumers of it can trace what the permissions correspond to
 * authInfo is an object with the following fields
 * - isAuthenticated
 * - isTrial
 * - isPremium
 * - isSuperAdmin
 * - isLicenseExpired

 * contentAccessStatus is an object with the following fields
 * - copyQuotaExceeded
 * - downloadQuotaExceeded
 * - redownloadMap ({} of uuid => bool)
 * these default to true since if something is wrong fetching this information, it's a product-level malfunction and we shouldn't allow copies/downloads to take place

 * returns a permissions object with the following boolean flags:
 * - block
 * - blockUnauthenticated
 * - blockTrial
 * - blockExpired
 * - blockQuota
 */
function actionPermissions(
  actionType,
  contentType,
  content,
  authInfo,
  contentAccessStatus = {
    copyQuotaExceeded: true,
    downloadQuotaExceeded: true,
    printQuotaExceeded: true,
    reportError: false
  }
) {
  // all actions are restricted if user is unauthenticated or expired
  var blockUnauthenticated = !authInfo.isAuthenticated
  var blockExpired = authInfo.isLicenseExpired

  var blockTrial
  var blockQuota

  var isDemo = content.demo

  // if action is favorite we don't need to look at counts/quotas; all that matters is whether user is unauthenticated or expired
  // otherwise, check for actions that are tracked with quotas
  if (actionType === ACTION_TYPES.FAVORITE) {
    blockTrial = false
    blockQuota = false
  } else if (actionType === ACTION_TYPES.COPY) {
    blockQuota =
      contentAccessStatus.copyQuotaExceeded &&
      !blockUnauthenticated &&
      !contentAccessStatus.reportError
  } else if (actionType === ACTION_TYPES.DOWNLOAD) {
    blockQuota =
      contentAccessStatus.downloadQuotaExceeded && !contentAccessStatus.redownloadMap[content.uuid]
  } else if (actionType === ACTION_TYPES.PRINT) {
    if (
      contentType === CONTENT_TYPES.SUPER_PAGE_ASSESSMENT ||
      contentType === CONTENT_TYPES.SAVED_GOAL
    ) {
      // printing anchor page assessments is a premium feature, so we want to show the premium blocker whether you're signed out or if you're trial user
      blockTrial = authInfo.isTrial || !authInfo.isAuthenticated
      blockUnauthenticated = false
    }
    blockQuota = contentAccessStatus.printQuotaExceeded && !contentAccessStatus.reportError
  } else if (actionType === ACTION_TYPES.SAVE || actionType === ACTION_TYPES.TRANSLATE) {
    blockQuota = false
    blockTrial = authInfo.isTrial
  } else if (actionType === ACTION_TYPES.GENERATE_ASSESSMENT) {
    // !isPremium rather than `isTrial || !isAuthenticated`: an authenticated user with no
    // license, or a license of some other sub_type, would slip through the latter.
    // blockUnauthenticated is left alone so a signed-out visitor is told to sign in.
    blockTrial = !authInfo.isPremium
    blockQuota = false
  } else if (
    actionType === ACTION_TYPES.FOLLOW_LINK ||
    actionType === ACTION_TYPES.COPY_STUDENT ||
    (actionType === ACTION_TYPES.SHARE && contentType === CONTENT_TYPES.STUDENT)
  ) {
    blockTrial = authInfo.isTrial || !authInfo.isAuthenticated
    blockUnauthenticated = false // if something is premium only, even if you're signed out we show the same messaging as a trial user
  }

  // Generation spends money and compute on every run, so demo content keeps its licence
  // check for it: the demo flag lets a prospect see a premium feature, not use it for free.
  var preserveEntitlementOnDemo = actionType === ACTION_TYPES.GENERATE_ASSESSMENT
  blockTrial = preserveEntitlementOnDemo ? blockTrial : !isDemo && blockTrial
  blockExpired = preserveEntitlementOnDemo ? blockExpired : !isDemo && blockExpired
  blockQuota = !isDemo && blockQuota
  blockUnauthenticated =
    (!isDemo || (isDemo && [ACTION_TYPES.FAVORITE, ACTION_TYPES.SAVE].indexOf(actionType) >= 0)) &&
    blockUnauthenticated

  return {
    // tracking/analytics fields
    actionType: actionType,
    contentType: contentType,
    contentTitle: content.title,
    contentUuid: content.uuid,

    // app access flags
    blockUnauthenticated,
    blockTrial,
    blockExpired,
    blockQuota,
    block:
      !authInfo.isSuperAdmin && (blockUnauthenticated || blockTrial || blockExpired || blockQuota)
  }
}

function isValidContentType(type) {
  return Object.values(CONTENT_TYPES).indexOf(type) > -1
}
